import BaseService from './Base';

/**
 * 定义K线服务类
 */
class KLineService extends BaseService {
    /**
     * K线服务类构造函数（接口文档：https://l.bte.top/app/doc.html）
     * @param {String} requestModule 请求的模块(kline和future——默认值)
     */
    constructor(requestModule) {
        super();
        this.module = requestModule || 'future';
    }

    /**
     * 获取K线数据
     * @param {Object} param 请求参数
     * @returns {Promise} 返回Promise对象
     */
    requestKLineData(param) {
        let parameter = {};
        ['base', 'quote', 'exchange', 'type', 'size'].forEach(key => {
            parameter[key] = param[key];
        });

        let urlObj;
        switch (this.module) {
            case 'kline':
                urlObj = {
                    api: this.api,
                    module: this.module,
                    action: 'line'
                };
                break;
            case 'future':
            default:
                urlObj = {
                    api: this.api,
                    module: this.module,
                    action: 'kline'
                };
                break;
        }

        return this.get(urlObj, parameter).then(response => {
            let result = {
                chartDatas: [], // 开盘价，收盘价，最低价，最高价
                times: [],
                opens: [],
                closes: [],
                lowests: [],
                highests: [],
                vols: []
            };
            response.kline.forEach(rawData => {
                let oneRow = [];
                ['time', 'open', 'close', 'lowest', 'highest', 'vol'].forEach(key => {
                    let value = rawData[key];
                    switch (key) {
                        case 'time':
                            // 因为echarts的markpoint里coord属性要求是字符串
                            value = rawData.date.toString();
                            break;
                        case 'lowest':
                            value = rawData.low;
                            break;
                        case 'highest':
                            value = rawData.high;
                            break;
                        default:
                            value = rawData[key];
                            break;
                    }
                    result[key + 's'].push(value);
                    if (key !== 'time') {
                        oneRow.push(value);
                    }
                });
                result.chartDatas.push(oneRow);
            });

            return {
                kline: result,
                price: response.ticker
            };
        });
    }

    /**
     * 获取K线压力线数据
     * @param {Object} param 请求参数
     * @returns {Promise} 返回Promise对象
     */
    requestPressureLineData(param) {
        let parameter = {};
        ['base', 'quote', 'exchange', 'type', 'size'].forEach(key => {
            parameter[key] = param[key];
        });
        return this.get(
            {
                api: this.api,
                module: 'future',
                action: 'resistance',
                id: 'line'
            },
            parameter
        ).then((datas = []) => {
            // 压力线暂时只取最大的两根线
            let results = [
                {
                    key: 'sellPrice5',
                    type: 'sell',
                    index: 4,
                    datas: []
                },
                {
                    key: 'buyPrice5',
                    type: 'buy',
                    index: 4,
                    datas: []
                }
            ];
            // // 从第一条数据中取出属性，判断有多少根压力线，并记录相关属性
            // Object.keys(datas[0]).forEach(key => {
            //     if (/(sell|buy)Price(\d+)/.test(key)) {
            //         results.push({
            //             key,
            //             type: RegExp.$1,
            //             index: Number(RegExp.$2) - 1, // 索引从0开始
            //             datas: []
            //         });
            //     }
            // });
            datas.forEach(data => {
                results.forEach(result => {
                    if (data[result.key] > 0) {
                        result.datas.push({
                            datetime: data.timestamp,
                            value: data[result.key]
                        });
                    }
                });
            });

            return results;
        });
    }

    /**
     * 获取K线净流量数据
     * @param {Object} param 请求参数
     * @returns {Promise} 返回Promise对象
     */
    getNetFlowData(param) {
        let parameter = {};
        ['base', 'quote', 'exchange', 'type', 'size'].forEach(key => {
            parameter[key] = param[key];
        });
        return this.get(
            {
                api: this.api,
                module: 'future',
                action: 'volume',
                id: 'line'
            },
            parameter
        ).then((datas = []) => {
            // 因为净流量数据缺失，需要按照K线的数据量补充，所以不在这里进行数据处理
            // return datas.map(data => {
            //     return data.netCount;
            // });
            return datas;
        });
    }

    /**
     * 获取K线实时解盘数据
     * @param {Object} param 请求的参数（base、quote）
     * @returns {Promise} 返回Promise对象
     */
    requestRealTimeSolutionData(param) {
        return this.post(
            {
                api: this.api,
                module: 'klineComment',
                action: 'getComments'
            },
            {
                symbol: param.base,
                pair: param.quote
            }
        ).then(response => {
            let result = [];
            if (response instanceof Array) {
                response.forEach(data => {
                    let content = data.content;
                    let option = {
                        time: data.klineDateTime.toString(), // 因为echarts的coord的X轴值需要字符串
                        isHighest: data.markerPlace,
                        content
                    };
                    result.push(option);
                });
            }
            return result;
        });
    }

    /**
     * 获取异常交易数据
     * @param {Object} param 请求参数
     * @returns {Promise} 返回Promise对象
     */
    requestAbnormalTransactionData(param) {
        let type = param.type;
        let base = param.base;

        let parameter = {};
        ['base', 'quote', 'exchange', 'type', 'size'].forEach(key => {
            parameter[key] = param[key];
        });
        return this.get(
            {
                api: this.api,
                module: 'future',
                action: 'abnormity',
                id: 'line'
            },
            parameter
        ).then((datas = []) => {
            let results = [];

            // 按照当前的时间单位计算对应的分钟数
            /(\d)(\w)/.exec(type);
            let minute = 1;
            let factor = Number(RegExp.$1);
            switch (RegExp.$2) {
                case 'h':
                    minute = 60;
                    break;
                case 'd':
                    minute = 1440;
                    break;
            }
            minute = minute * factor;

            datas.forEach(data => {
                let typeMapData = {};
                data.orders.forEach(order => {
                    typeMapData[order.orderType] = order;
                });
                // 压单和托单只画两者成交量最大的那个
                if (typeMapData.buy && typeMapData.sell) {
                    if (typeMapData.sell.count > typeMapData.buy.count) {
                        typeMapData.buy.isNotDraw = true;
                    } else {
                        typeMapData.sell.isNotDraw = true;
                    }
                }
                results = results.concat(
                    Object.keys(typeMapData).map(key => {
                        let order = typeMapData[key];
                        order.computedCount = order.count / minute;
                        // 因为BTC是$100/张
                        if (base === 'BTC') {
                            order.computedCount *= 10;
                        }
                        return {
                            time: data.datetime.toString(),
                            ...order
                        };
                    })
                );
            });
            return results;
        });
    }
}

export default KLineService;



// WEBPACK FOOTER //
// ./src/service/KLine.js